'use client'

import { useEffect } from 'react'

/**
 * Client-side performance monitoring
 * Reports Core Web Vitals - only logs final values to reduce console spam
 */

export function PerformanceMonitor() {
  useEffect(() => {
    // Only in production
    if (process.env.NODE_ENV !== 'production') return

    // Monitor Core Web Vitals
    if (typeof window !== 'undefined' && 'PerformanceObserver' in window) {
      try {
        // Largest Contentful Paint (LCP) - only log once
        let lcpLogged = false
        const lcpObserver = new PerformanceObserver((list) => {
          if (lcpLogged) return
          const entries = list.getEntries()
          const lastEntry = entries[entries.length - 1] as PerformanceEntry & {
            renderTime?: number
            loadTime?: number
          }
          const lcpTime = lastEntry.renderTime || lastEntry.loadTime
          if (lcpTime && lcpTime > 2500) {
            console.warn('[LCP]', `${lcpTime}ms (target: <2500ms)`)
          }
          lcpLogged = true
        })
        lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true })

        // First Input Delay (FID) - only log if slow
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries()
          entries.forEach((entry) => {
            // @ts-expect-error - processingStart exists on PerformanceEventTiming
            const fid = entry.processingStart - entry.startTime
            if (fid > 100) {
              console.warn('[FID]', `${fid}ms (target: <100ms)`)
            }
          })
        })
        fidObserver.observe({ type: 'first-input', buffered: true })

        // Cumulative Layout Shift (CLS) - only log final value if bad
        let clsValue = 0
        const clsObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            // @ts-expect-error - value exists on LayoutShift
            if (!entry.hadRecentInput) {
              // @ts-expect-error - value exists on LayoutShift
              clsValue += entry.value
            }
          }
        })
        clsObserver.observe({ type: 'layout-shift', buffered: true })

        // Log CLS only once after page is interactive
        setTimeout(() => {
          if (clsValue > 0.1) {
            console.warn('[CLS]', clsValue.toFixed(4), '(target: <0.1)')
          }
        }, 3000)

        // Log slow resources - only extremely slow ones
        const resourceObserver = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            const resourceEntry = entry as PerformanceEntry & {
              duration?: number
              initiatorType?: string
            }
            // Only log resources slower than 2 seconds
            if (resourceEntry.duration && resourceEntry.duration > 2000) {
              console.warn('[SLOW RESOURCE]', {
                name: entry.name.split('/').pop(),
                duration: `${resourceEntry.duration.toFixed(0)}ms`,
                type: resourceEntry.initiatorType,
              })
            }
          })
        })
        resourceObserver.observe({ type: 'resource', buffered: true })
      } catch (error) {
        console.error('Performance monitoring error:', error)
      }
    }
  }, [])

  return null
}
